import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { getDrawingMarkers, getVideoCommentMarkers } from '../../api';
import { COMMENT } from '../../enums';
import { isVisibleComment } from '../../helpers/comments';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateVideoControls, updateVideoPlayedSeconds, updateTimelineSeconds, updateSortedSelectedClips, triggerRefresh } from '../../store/videos/videoSlice';
import { toFollowPlayhead } from '../../store/sharedStore/sharedStoreSlice';
import { useClipFavourites } from '../ClipFavourites/context';
import { useClipFilter } from '../ClipFilter/context';

const useMarkerState = () => {
  const { feedId, selection } = useClipFavourites();
  const { sidebarVisible, setSidebarVisible, setSidebarView } = useClipFilter();
  const dispatch = useAppDispatch();
  const videoControls = useAppSelector((state) => state.videoStore.videoControls);
  const notification = useSnackbar();
  const commentRefresh = useAppSelector((state) => state.composersStore.commentFetchTrigger);
  const [revision, setRevision] = useState(0);
  const [drawingMinHeight, setDrawingMinHeight] = useState(0);
  const [seekSequence, setSeekSequence] = useState(0);
  const notifySeek = useCallback(() => setSeekSequence((value) => value + 1), []);
  const [markers, setMarkers] = useState<COMMENT[]>([]);
  const [videoComments, setVideoComments] = useState<COMMENT[]>([]);
  const [markersLoaded, setMarkersLoaded] = useState(false);
  const [request, setRequest] = useState<{ comment: COMMENT; sequence: number; automatic: boolean } | null>(null);
  const [videoCommentSeek, setVideoCommentSeek] = useState<{ feedId: string; seconds: number } | null>(null);
  const nextRequest = useRef(0);
  const consumedEditRequest = useRef<number>();
  const consumeDiscussionEdit = useCallback((sequence: number) => {
    if (consumedEditRequest.current === sequence) return false;
    consumedEditRequest.current = sequence;
    return true;
  }, []);
  const [discussionRequest, setDiscussionRequest] = useState<{ comment?: COMMENT; clipId?: string; edit?: boolean; restoreSidebar?: boolean; sequence: number } | null>(null);
  const dismissDiscussion = useCallback(() => { setDiscussionRequest(null); setRequest(null); }, []);
  const openDiscussion = (comment?: COMMENT, clipId?: string, edit = false) => {
    setDiscussionRequest({ comment, edit, restoreSidebar: edit && !sidebarVisible,
      clipId: clipId ?? (!comment && selection.length ? String(selection[0].id) : undefined), sequence: ++nextRequest.current });
    setSidebarView('comments'); setSidebarVisible(true);
  };
  const finishDiscussionEdit = (sequence: number, resume = false) => {
    if (!discussionRequest?.edit || discussionRequest.sequence !== sequence) return;
    if (discussionRequest.restoreSidebar) setSidebarVisible(false);
    setDiscussionRequest({ ...discussionRequest, edit: false, restoreSidebar: false });
    if (resume) {
      dispatch(toFollowPlayhead(true));
      dispatch(updateVideoControls({ ...videoControls, playing: true }));
    }
  };
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const showMarker = useCallback((comment: COMMENT, automatic = false) => {
    setVideoCommentSeek(null);
    const marker = markers.find((item) => String(item.id) === String(comment.id));
    const resolved = {
      ...comment,
      ...marker,
      clip_id: marker ? marker.clip_id : comment.clip_id ?? (comment.parent_type === 'clip' ? comment.parent_id : null),
      clip: marker ? marker.clip : comment.clip,
      can_edit_drawing: marker?.can_edit_drawing === true,
    };
    setRequest({ comment: resolved, sequence: ++nextRequest.current, automatic });
    if (!automatic) {
      setDiscussionRequest({ comment: resolved, sequence: ++nextRequest.current });
      setSidebarView('comments'); setSidebarVisible(true);
    }
  }, [markers, setSidebarView, setSidebarVisible]);
  const dismissRequest = useCallback(() => setRequest(null), []);
  const showVideoComment = (comment: COMMENT) => {
    const seconds = comment.timestamp === 0 ? 0.1 : comment.timestamp;
    setRequest(null);
    dispatch(updateSortedSelectedClips([]));
    dispatch(updateVideoControls({ ...videoControls, playedSeconds: seconds, playing: false }));
    dispatch(updateVideoPlayedSeconds(seconds));
    dispatch(updateTimelineSeconds(seconds));
    dispatch(toFollowPlayhead(true));
    dispatch(triggerRefresh());
    setVideoCommentSeek({ feedId, seconds });
    openDiscussion(comment);
  };

  useEffect(() => { setMarkers([]); setVideoComments([]); setMarkersLoaded(false); setRequest(null); setVideoCommentSeek(null); setDiscussionRequest(null); }, [feedId]);

  useEffect(() => {
    if (!feedId) return;
    let cancelled = false;
    getVideoCommentMarkers(feedId).then(({ data }) => {
      if (!cancelled) setVideoComments(data);
    }).catch(() => {
      if (!cancelled) notification.showMessage('Could not load video comment markers');
    });
    return () => { cancelled = true; };
  }, [feedId, revision, commentRefresh]);

  useEffect(() => {
    if (!feedId) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;
    const load = async (attempt = 0) => {
      try {
        const { data } = await getDrawingMarkers(feedId);
        if (cancelled) return;
        setMarkers(data.filter((comment) => isVisibleComment(comment) && !!comment.drawing)
          .sort((a, b) => a.timestamp - b.timestamp || Number(a.id) - Number(b.id)));
        setMarkersLoaded(true);
      } catch {
        if (cancelled) return;
        if (attempt < 2) retryTimer = setTimeout(() => load(attempt + 1), 1000 * (attempt + 1));
        else notification.showMessage('Could not load drawing markers');
      }
    };
    void load();
    return () => { cancelled = true; clearTimeout(retryTimer); };
  }, [feedId, revision, commentRefresh]);

  return { markers, markersLoaded, request, showMarker, dismissRequest, refresh, discussionRequest, dismissDiscussion, openDiscussion, consumeDiscussionEdit, finishDiscussionEdit,
    drawingMinHeight, setDrawingMinHeight, videoComments, showVideoComment, videoCommentSeek, seekSequence, notifySeek };
};

const Context = createContext<ReturnType<typeof useMarkerState> | null>(null);
export const VideoDrawingProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useMarkerState();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
export const useDrawingMarkers = () => {
  const value = useContext(Context);
  if (!value) throw new Error('Drawing markers require a video page');
  return value;
};
